import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IMAGES, TERRITORY_MAP, TERRITORY_WAR, TERRITORY_REWARDS } from '../mock/data';
import { Reveal, Eyebrow, SectionHeading } from '../components/xc/Bits';
import { InvestorNote } from '../components/xc/InvestorNote';
import { ArrowLeft, ShieldCheck, Lock, Flag, Coins, Trophy, Building2, Swords, Timer, MapPin } from 'lucide-react';

const REWARD_ICONS = { Coins, Flag, Trophy, Building2 };

function fmt(s) {
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
}

function ActiveWar() {
  const [atk, setAtk] = useState(TERRITORY_WAR.attacker.influence);
  const [left, setLeft] = useState(TERRITORY_WAR.endsInSeconds);
  const [events, setEvents] = useState([{ id: 0, text: 'War declared on The Port — influence shifts with every qualifying round' }]);

  useEffect(() => {
    const clock = setInterval(() => setLeft((s) => Math.max(0, s - 1)), 1000);
    const push = setInterval(() => {
      const delta = (Math.random() > 0.5 ? 1 : -1) * (Math.random() * 1.4);
      setAtk((v) => Math.min(58, Math.max(42, +(v + delta).toFixed(1))));
      const pool = [
        'The Syndicate lands a coordinated push at the docks',
        'Iron Docks reinforces the cargo terminal',
        'Wolf banks a 220-point qualifying round for The Syndicate',
        'Iron Docks crew chains three rounds in a row',
        'Anna88 flips a warehouse checkpoint',
        'Iron Docks holds the crane line',
      ];
      setEvents((e) => [{ id: Date.now(), text: pool[Math.floor(Math.random() * pool.length)] }, ...e].slice(0, 5));
    }, 3200);
    return () => { clearInterval(clock); clearInterval(push); };
  }, []);

  const a = TERRITORY_WAR.attacker, d = TERRITORY_WAR.defender;
  return (
    <div data-testid="territory-active-war" className="xc-card rounded-2xl overflow-hidden">
      <div className="px-6 py-4 border-b border-white/8 flex flex-wrap items-center justify-between gap-2">
        <span className="flex items-center gap-2 text-[11px] font-600 tracking-[0.2em] text-[#FF204E]">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#FF204E] opacity-60" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-[#FF204E]" />
          </span>
          ACTIVE WAR — {TERRITORY_WAR.district.toUpperCase()}
        </span>
        <span data-testid="territory-war-countdown" className="flex items-center gap-1.5 text-xs text-[#E0B85A] tabular-nums"><Timer className="h-3.5 w-3.5" /> Ends in {fmt(left)}</span>
      </div>
      <div className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-white/40">Attacker</div>
            <div className="font-display font-700 text-white text-lg">{a.name}</div>
            <div className="font-display font-700 text-2xl tabular-nums" style={{ color: a.color }}>{atk.toFixed(1)}%</div>
          </div>
          <Swords className="h-8 w-8 text-white/25" />
          <div className="text-right">
            <div className="text-[11px] uppercase tracking-wider text-white/40">Defender</div>
            <div className="font-display font-700 text-white text-lg">{d.name}</div>
            <div className="font-display font-700 text-2xl tabular-nums" style={{ color: d.color }}>{(100 - atk).toFixed(1)}%</div>
          </div>
        </div>
        <div className="mt-5 h-3 rounded-full bg-white/8 overflow-hidden flex">
          <div className="h-full transition-all duration-1000" style={{ width: `${atk}%`, background: `linear-gradient(90deg, ${a.color}, ${a.color}88)` }} />
          <div className="h-full flex-1 transition-all duration-1000" style={{ background: `linear-gradient(90deg, ${d.color}88, ${d.color})` }} />
        </div>
        <p className="mt-2 text-[11px] text-white/35 text-center">Influence shifts with qualifying casino activity from both crews. Majority at the deadline takes the district.</p>
        <div data-testid="territory-war-feed" className="mt-5 space-y-2">
          {events.map((e) => (
            <div key={e.id} className="flex items-center gap-2 text-sm xc-fade-up">
              <Flag className="h-3.5 w-3.5 text-[#B026FF] flex-shrink-0" />
              <span className="text-white/60">{e.text}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function DistrictCard({ t, i }) {
  const statusColor = { Held: '#2FBF71', Contested: '#E0B85A', War: '#FF204E', Locked: '#5b5b66' }[t.status];
  return (
    <Reveal delay={i * 70}>
      <div data-testid={`territory-district-${t.id}`} className={`xc-card rounded-2xl p-5 h-full ${t.status === 'Locked' ? 'opacity-60' : ''}`}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 font-display font-700 text-white"><MapPin className="h-4 w-4 text-white/40" /> {t.name}</div>
          <span className="text-[10px] font-600 tracking-[0.14em] uppercase px-2.5 py-1 rounded-full" style={{ color: statusColor, border: `1px solid ${statusColor}55`, background: `${statusColor}12` }}>{t.status}</span>
        </div>
        {t.status === 'Locked' ? (
          <div className="flex items-center gap-2 text-sm text-white/40 mt-6"><Lock className="h-4 w-4" /> Unlocks at city level {t.unlockLevel}</div>
        ) : (
          <>
            <div className="text-sm text-white/55">Held by <span className="font-600" style={{ color: t.color }}>{t.holder}</span>{t.self && <span className="text-[#E0B85A]"> — your crew</span>}</div>
            {t.challenger && <div className="text-xs text-white/40 mt-1">Challenged by {t.challenger}</div>}
            <div className="mt-4">
              <div className="flex justify-between text-[11px] text-white/40 mb-1"><span>Influence</span><span className="tabular-nums">{t.influence}%</span></div>
              <div className="h-2 rounded-full bg-white/8 overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${t.influence}%`, background: `linear-gradient(90deg, ${t.color}, ${t.color}77)`, boxShadow: `0 0 10px ${t.color}66` }} />
              </div>
            </div>
            <div className="mt-3 flex items-center gap-1.5 text-xs text-[#E0B85A]"><Coins className="h-3.5 w-3.5" /> {t.income} LC / day to crew pool</div>
          </>
        )}
      </div>
    </Reveal>
  );
}

export default function TerritoryWars() {
  const nav = useNavigate();
  return (
    <div className="bg-[#070609] min-h-screen">
      <section className="relative overflow-hidden border-b border-white/5">
        <img src={IMAGES.foggyCity} alt="" className="absolute inset-0 h-full w-full object-cover opacity-35" />
        <div className="absolute inset-0 city-overlay" />
        <div className="relative max-w-[1440px] mx-auto px-4 lg:px-8 py-14">
          <button data-testid="territory-back-btn" onClick={() => nav('/missions')} className="flex items-center gap-2 text-sm text-white/50 hover:text-white transition-colors mb-6"><ArrowLeft className="h-4 w-4" /> Mission Board</button>
          <Eyebrow color="#B026FF">District Control — Preview</Eyebrow>
          <h1 className="font-display font-700 text-3xl sm:text-4xl md:text-5xl text-white tracking-tight">Territory <span className="text-gradient-rp">Wars</span></h1>
          <p className="mt-3 text-white/60 max-w-xl">Crews compete for visible control of Xincity districts. Held territory pays daily Loyalty Coins, glows in your crew's colors and decides who really owns the city.</p>
          <div className="mt-5 inline-flex items-center gap-2 text-xs text-white/40 xc-glass rounded-full px-4 py-2"><ShieldCheck className="h-3.5 w-3.5 text-[#2FBF71]" /> Investor preview — simulated war with demo data. No real money.</div>
          <div className="mt-4"><InvestorNote id="territory" title="Why territory creates daily habits">
            Held territory decays without defence and pays the crew pool daily — a classic appointment mechanic. Crews log in every day to protect what they own, turning weekly casino visitors into daily citizens of the city.
          </InvestorNote></div>
        </div>
      </section>

      <section className="max-w-[1440px] mx-auto px-4 lg:px-8 py-10 grid lg:grid-cols-[420px_1fr] gap-6">
        <Reveal><ActiveWar /></Reveal>
        <div>
          <Reveal delay={80}><SectionHeading eyebrow="City control map" color="#B026FF" title="Who holds Xincity tonight" /></Reveal>
          <div className="grid sm:grid-cols-2 gap-4 mt-6">
            {TERRITORY_MAP.map((t, i) => <DistrictCard key={t.id} t={t} i={i} />)}
          </div>
        </div>
      </section>

      <section className="max-w-[1440px] mx-auto px-4 lg:px-8 pb-16">
        <Reveal><SectionHeading eyebrow="Why territory matters" color="#E0B85A" title="Control is status" center /></Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-10">
          {TERRITORY_REWARDS.map((r, i) => {
            const Icon = REWARD_ICONS[r.icon];
            return (
              <Reveal key={r.title} delay={i * 80}>
                <div className="xc-card rounded-2xl p-6 h-full">
                  <Icon className="h-6 w-6 text-[#E0B85A] mb-3" />
                  <h3 className="font-display font-700 text-white">{r.title}</h3>
                  <p className="text-sm text-white/50 mt-2 leading-relaxed">{r.desc}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>
    </div>
  );
}
